/**
 * Definición de tours por página. Cada entrada es una clave que el
 * código usa con `useStartTour(key)`. Los `target` apuntan a elementos
 * marcados con `data-tour="..."` en cada página.
 *
 * Reglas:
 *  - El primer paso suele apuntar al body (placement center) como
 *    bienvenida — joyride lo permite con target 'body'.
 *  - `disableBeacon: true` arranca el tour directamente sin pedir
 *    click en el "beacon" pulsante.
 *  - `placement: 'auto'` deja a joyride decidir el lado mejor según
 *    espacio disponible.
 */

const ENDING = "Puedes volver a este tour desde el botón de ayuda (?) del Topbar.";

export const TOURS = {
  dashboard: [
    {
      target: "body",
      placement: "center",
      title: "¡Bienvenido a Kronaly!",
      content:
        "Este es tu Dashboard — el resumen de tu día y de lo pendiente. Te enseño las cosas clave en 30 segundos.",
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-kpis"]',
      title: "Indicadores clave",
      content:
        "De un vistazo: quién está trabajando, ausencias del día, solicitudes pendientes.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-activity"]',
      title: "Tu semana",
      content:
        "Las horas trabajadas vs planificadas de los últimos 7 días.",
      placement: "auto",
    },
    {
      target: '[data-tour="dashboard-readings"]',
      title: "Lecturas obligatorias",
      content:
        "Si tienes documentos internos pendientes de acusar, aparecerán aquí.",
      placement: "auto",
    },
    {
      target: "body",
      placement: "center",
      title: "Listo",
      content: ENDING,
    },
  ],

  jornada: [
    {
      target: "body",
      placement: "center",
      title: "Mi jornada",
      content:
        "Aquí controlas tu fichaje del día: entrada, pausas y salida.",
      disableBeacon: true,
    },
    {
      target: '[data-tour="jornada-counter"]',
      title: "Contador en directo",
      content:
        "El tiempo que llevas trabajando hoy. Se actualiza en vivo mientras estás fichado.",
      placement: "auto",
    },
    {
      target: '[data-tour="jornada-actions"]',
      title: "Marcar entrada, pausa y salida",
      content:
        "Botón principal para iniciar/finalizar la jornada. El secundario es para pausas.",
      placement: "top",
    },
    {
      target: '[data-tour="jornada-minstats"]',
      title: "Tus marcajes del día",
      content:
        "Resumen de la jornada actual: entrada, salida prevista y total neto.",
      placement: "top",
    },
    {
      target: "body",
      placement: "center",
      title: "Listo",
      content: ENDING,
    },
  ],

  ausencias: [
    {
      target: "body",
      placement: "center",
      title: "Ausencias",
      content:
        "Solicita vacaciones, permisos o bajas, y consulta tu balance.",
      disableBeacon: true,
    },
    {
      target: '[data-tour="ausencias-balance"]',
      title: "Balance de vacaciones",
      content:
        "Días disponibles, aprobados y pendientes del ciclo en curso.",
      placement: "auto",
    },
    {
      target: '[data-tour="ausencias-add"]',
      title: "Añadir ausencia",
      content:
        "Crea una nueva solicitud. Vas a poder elegir tipo, fechas y motivo.",
      placement: "auto",
    },
    {
      target: '[data-tour="ausencias-list"]',
      title: "Tus solicitudes",
      content:
        "Histórico de tus ausencias con su estado: aprobada, pendiente o rechazada.",
      placement: "top",
    },
    {
      target: "body",
      placement: "center",
      title: "Listo",
      content: ENDING,
    },
  ],

  historial: [
    {
      target: "body",
      placement: "center",
      title: "Historial de jornadas",
      content:
        "Consulta tus jornadas trabajadas por semana o mes y el balance horario.",
      disableBeacon: true,
    },
    {
      target: '[data-tour="historial-filters"]',
      title: "Filtros de periodo",
      content: "Cambia entre vista semanal o mensual y navega por fechas.",
      placement: "bottom",
    },
    {
      target: '[data-tour="historial-totals"]',
      title: "Totales del periodo",
      content:
        "Horas trabajadas, planificadas y balance (positivo o negativo) del periodo.",
      placement: "auto",
    },
    {
      target: '[data-tour="historial-days"]',
      title: "Detalle por día",
      content:
        "Cada día muestra entradas, pausas y diferencias frente a lo planificado.",
      placement: "top",
    },
    {
      target: "body",
      placement: "center",
      title: "Listo",
      content: ENDING,
    },
  ],

  solicitudes: [
    {
      target: "body",
      placement: "center",
      title: "Solicitudes",
      content:
        "Aquí gestionas tus horas extra: pedirlas, ver el estado, cancelar pendientes.",
      disableBeacon: true,
    },
    {
      target: '[data-tour="solicitudes-tabs"]',
      title: "Mis solicitudes / a aprobar",
      content:
        "Si eres manager verás también las que debes aprobar. Si no, solo verás las tuyas.",
      placement: "bottom",
    },
    {
      target: '[data-tour="solicitudes-list"]',
      title: "Listado",
      content:
        "Cada fila muestra estado, minutos solicitados y motivo. Click para detalles.",
      placement: "top",
    },
    {
      target: "body",
      placement: "center",
      title: "Listo",
      content: ENDING,
    },
  ],
};

export const TOUR_LABELS = {
  dashboard: "Dashboard",
  jornada: "Mi jornada",
  ausencias: "Ausencias",
  historial: "Historial",
  solicitudes: "Solicitudes",
};

/** Mapea pathname a clave de tour. null si la página no tiene tour. */
export function tourKeyForPath(pathname) {
  if (!pathname) return null;
  if (pathname === "/dashboard" || pathname === "/") return "dashboard";
  if (pathname.startsWith("/jornada/historial")) return "historial";
  if (pathname.startsWith("/jornada")) return "jornada";
  if (pathname.startsWith("/ausencias")) return "ausencias";
  if (pathname.startsWith("/solicitudes")) return "solicitudes";
  return null;
}
